import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Select } from '@/shared/ui/selects/Select';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, isApiError } from '@/shared/api/errors';
import {
  apiCategoriesDelete,
  apiCategoriesSubcategoryDelete,
  useCategories,
} from '@/entities/categories';
import {
  FeaturesCategoriesCategoryFormDialog,
  FeaturesCategoriesSubcategoryFormDialog,
} from '@/features/categories';
import type {
  ApiCategoriesGetItem,
  ApiCategoriesGetSubcategory,
} from '@/shared/types/api/categories';
import { cn } from '@/shared/utils/cn';

type SubDeleteTarget = { categoryId: string; sub: ApiCategoriesGetSubcategory };

// Backend returns HTTP 400 with a message mentioning `target` when the
// category/subcategory is still referenced by products and no reassignment
// destination was supplied.
const isTargetRequiredError = (err: unknown): boolean =>
  isApiError(err) &&
  err.response?.status === 400 &&
  extractErrorMessage(err).toLowerCase().includes('target');

export const WidgetsCategoriesMain = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const canCreate = usePerm('categories.create');
  const canEdit = usePerm('categories.edit');
  const canDelete = usePerm('categories.delete');
  const query = useCategories();

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingCat, setEditingCat] = useState<ApiCategoriesGetItem | null>(null);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<{
    categoryId: string;
    sub: ApiCategoriesGetSubcategory | null;
  } | null>(null);
  const [catToDelete, setCatToDelete] = useState<ApiCategoriesGetItem | null>(null);
  const [subToDelete, setSubToDelete] = useState<SubDeleteTarget | null>(null);
  // Reassignment flow: when a delete fails because the item is in use, the admin
  // must pick a destination category/subcategory for the referencing products.
  const [reassignCat, setReassignCat] = useState<ApiCategoriesGetItem | null>(null);
  const [reassignSub, setReassignSub] = useState<SubDeleteTarget | null>(null);
  const [reassignTarget, setReassignTarget] = useState('');

  const closeReassign = (): void => {
    setReassignCat(null);
    setReassignSub(null);
    setReassignTarget('');
  };

  const deleteCat = useMutate({
    api: ({ id, target }: { id: string; target?: string }) => apiCategoriesDelete(id, target),
    invalidateKeys: [queryKeys.categories.all],
    onSuccess: () => {
      toast.info(t('categories.toast.deleted'));
      setCatToDelete(null);
      closeReassign();
    },
    onError: (e, m) => {
      if (isTargetRequiredError(e) && catToDelete) {
        setReassignCat(catToDelete);
        setCatToDelete(null);
        setReassignTarget('');
      } else {
        toast.error(m);
      }
    },
  });

  const deleteSub = useMutate({
    api: ({ categoryId, subId, target }: { categoryId: string; subId: string; target?: string }) =>
      apiCategoriesSubcategoryDelete(categoryId, subId, target),
    invalidateKeys: [queryKeys.categories.all],
    onSuccess: () => {
      toast.info(t('categories.toast.subDeleted'));
      setSubToDelete(null);
      closeReassign();
    },
    onError: (e, m) => {
      if (isTargetRequiredError(e) && subToDelete) {
        setReassignSub(subToDelete);
        setSubToDelete(null);
        setReassignTarget('');
      } else {
        toast.error(m);
      }
    },
  });

  const catReassignTargets = (query.data ?? []).filter((c) => c.id !== reassignCat?.id);
  const subReassignTargets = reassignSub
    ? ((query.data ?? []).find((c) => c.id === reassignSub.categoryId)?.subcategories ?? []).filter(
        (s) => s.id !== reassignSub.sub.id,
      )
    : [];

  const toggle = (id: string): void => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <>
      <PageHeader
        title={t('categories.list.title')}
        subtitle={t('categories.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditingCat(null);
                setCatDialogOpen(true);
              }}
              title={t('categories.list.newCategory')}
              aria-label={t('categories.list.newCategory')}
            >
              <span className="hidden sm:inline">{t('categories.list.newCategory')}</span>
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('categories.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : query.data?.length === 0 ? (
          <EmptyState
            icon={<Tag className="h-8 w-8" />}
            title={t('categories.list.emptyTitle')}
            description={t('categories.list.emptyDescription')}
          />
        ) : (
          <div className="space-y-2">
            {(query.data ?? []).map((cat) => {
              const isOpen = expanded[cat.id] ?? false;
              return (
                <Section key={cat.id} className="!p-0">
                  <div className="flex items-center gap-2 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggle(cat.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100"
                      aria-expanded={isOpen}
                      aria-label={isOpen ? t('categories.list.collapse') : t('categories.list.expand')}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-ink">{cat.name_uz}</p>
                      <p className="truncate text-xs text-ink-muted">
                        {[
                          cat.name_ru,
                          cat.name_en,
                          cat.name_kk,
                          t('categories.list.subcategoriesCount', { count: cat.subcategories.length }),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    {canEdit && (
                      <IconButton
                        label={t('common.edit')}
                        onClick={() => {
                          setEditingCat(cat);
                          setCatDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                    )}
                    {canCreate && (
                      <IconButton
                        label={t('categories.list.addSubcategory')}
                        onClick={() => setEditingSub({ categoryId: cat.id, sub: null })}
                      >
                        <Plus className="h-4 w-4 text-brand-600" />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        label={t('common.delete')}
                        variant="danger"
                        onClick={() => setCatToDelete(cat)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    )}
                  </div>
                  {isOpen && (
                    <ul className={cn('border-t border-slate-100 p-2')}>
                      {cat.subcategories.length === 0 ? (
                        <li className="px-3 py-3 text-sm text-ink-muted">
                          {t('categories.list.emptySubcategories')}
                        </li>
                      ) : (
                        cat.subcategories.map((sub) => (
                          <li
                            key={sub.id}
                            className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-slate-50"
                          >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-ink-faint">
                              <Tag className="h-3 w-3" aria-hidden />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm text-ink-soft">{sub.name_uz}</p>
                              <p className="truncate text-xs text-ink-muted">
                                {[sub.name_ru, sub.name_en, sub.name_kk].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                            {canEdit && (
                              <IconButton
                                label={t('common.edit')}
                                onClick={() => setEditingSub({ categoryId: cat.id, sub })}
                              >
                                <Pencil className="h-4 w-4" />
                              </IconButton>
                            )}
                            {canDelete && (
                              <IconButton
                                label={t('common.delete')}
                                variant="danger"
                                onClick={() => setSubToDelete({ categoryId: cat.id, sub })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </IconButton>
                            )}
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </Section>
              );
            })}
          </div>
        )}
      </div>

      <FeaturesCategoriesCategoryFormDialog
        open={catDialogOpen}
        editing={editingCat}
        onClose={() => {
          setCatDialogOpen(false);
          setEditingCat(null);
        }}
      />
      <FeaturesCategoriesSubcategoryFormDialog
        open={Boolean(editingSub)}
        categoryId={editingSub?.categoryId ?? ''}
        editing={editingSub?.sub ?? null}
        onClose={() => setEditingSub(null)}
      />

      <ConfirmDialog
        open={Boolean(catToDelete)}
        title={t('categories.confirmDelete.title')}
        description={t('categories.confirmDelete.description', { name: catToDelete?.name_uz ?? '' })}
        confirmLabel={t('categories.confirmDelete.confirm')}
        confirmVariant="danger"
        loading={deleteCat.isPending}
        onCancel={() => setCatToDelete(null)}
        onConfirm={() => catToDelete && deleteCat.mutate({ id: catToDelete.id })}
      />
      <ConfirmDialog
        open={Boolean(subToDelete)}
        title={t('categories.confirmDeleteSub.title')}
        description={t('categories.confirmDeleteSub.description', { name: subToDelete?.sub.name_uz ?? '' })}
        confirmLabel={t('categories.confirmDeleteSub.confirm')}
        confirmVariant="danger"
        loading={deleteSub.isPending}
        onCancel={() => setSubToDelete(null)}
        onConfirm={() =>
          subToDelete &&
          deleteSub.mutate({ categoryId: subToDelete.categoryId, subId: subToDelete.sub.id })
        }
      />

      <Dialog
        open={Boolean(reassignCat)}
        onClose={closeReassign}
        title={t('categories.reassign.title', { defaultValue: 'Mahsulotlarni koʻchirish' })}
        description={t('categories.reassign.catDescription', {
          name: reassignCat?.name_uz ?? '',
          defaultValue:
            '"{{name}}" kategoriyasi mahsulotlarda ishlatilmoqda. Oʻchirishdan oldin mahsulotlarni boshqa kategoriyaga koʻchiring.',
        })}
        footer={
          <>
            <Button variant="outline" onClick={closeReassign} disabled={deleteCat.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={deleteCat.isPending}
              disabled={!reassignTarget || deleteCat.isPending}
              onClick={() =>
                reassignCat &&
                reassignTarget &&
                deleteCat.mutate({ id: reassignCat.id, target: reassignTarget })
              }
            >
              {t('categories.reassign.confirm', { defaultValue: 'Koʻchirish va oʻchirish' })}
            </Button>
          </>
        }
      >
        {catReassignTargets.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {t('categories.reassign.catNoTargets', {
              defaultValue: 'Koʻchirish uchun boshqa kategoriya yoʻq. Avval kategoriya yarating.',
            })}
          </p>
        ) : (
          <Select
            label={t('categories.reassign.catLabel', { defaultValue: 'Yangi kategoriya' })}
            placeholder={t('categories.reassign.placeholder', { defaultValue: 'Tanlang' })}
            value={reassignTarget}
            onChange={(e) => setReassignTarget(e.target.value)}
            options={catReassignTargets.map((c) => ({ value: c.id, label: c.name_uz }))}
          />
        )}
      </Dialog>

      <Dialog
        open={Boolean(reassignSub)}
        onClose={closeReassign}
        title={t('categories.reassign.title', { defaultValue: 'Mahsulotlarni koʻchirish' })}
        description={t('categories.reassign.subDescription', {
          name: reassignSub?.sub.name_uz ?? '',
          defaultValue:
            '"{{name}}" subkategoriyasi mahsulotlarda ishlatilmoqda. Oʻchirishdan oldin mahsulotlarni boshqa subkategoriyaga koʻchiring.',
        })}
        footer={
          <>
            <Button variant="outline" onClick={closeReassign} disabled={deleteSub.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={deleteSub.isPending}
              disabled={!reassignTarget || deleteSub.isPending}
              onClick={() =>
                reassignSub &&
                reassignTarget &&
                deleteSub.mutate({
                  categoryId: reassignSub.categoryId,
                  subId: reassignSub.sub.id,
                  target: reassignTarget,
                })
              }
            >
              {t('categories.reassign.confirm', { defaultValue: 'Koʻchirish va oʻchirish' })}
            </Button>
          </>
        }
      >
        {subReassignTargets.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {t('categories.reassign.subNoTargets', {
              defaultValue:
                'Koʻchirish uchun boshqa subkategoriya yoʻq. Avval subkategoriya yarating.',
            })}
          </p>
        ) : (
          <Select
            label={t('categories.reassign.subLabel', { defaultValue: 'Yangi subkategoriya' })}
            placeholder={t('categories.reassign.placeholder', { defaultValue: 'Tanlang' })}
            value={reassignTarget}
            onChange={(e) => setReassignTarget(e.target.value)}
            options={subReassignTargets.map((s) => ({ value: s.id, label: s.name_uz }))}
          />
        )}
      </Dialog>
    </>
  );
};
